import { useContext, useMemo, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Button,
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { Add, Business } from '@mui/icons-material';
import * as cuitProfilesApi from '@/api/cuitProfiles';
import type { AfipEnvironment, CuitProfileResponse, CuitProfileStatus } from '@/api/types';
import { ProductContext } from '@/auth/ProductContext';
import { LoadingScreen } from '@/components/LoadingScreen';
import { EmptyState } from '@/components/EmptyState';
import { CuitProfileCard } from '@/components/CuitProfileCard';
import { filterCuitProfilesForCommercialProduct } from '@/lib/cuitProfileScope';
import {
  AFIP_ENVIRONMENT_LABEL,
  CUIT_PROFILE_STATUS_LABEL,
} from '@/lib/labels';

export const Route = createFileRoute('/_authenticated/cuit-profiles/')({
  component: CuitProfilesListPage,
});

function CuitProfilesListPage(): JSX.Element {
  const navigate = useNavigate();
  const product = useContext(ProductContext);
  const [environmentFilter, setEnvironmentFilter] = useState<AfipEnvironment | 'All'>('All');
  const [statusFilter, setStatusFilter] = useState<CuitProfileStatus | 'All'>('All');
  const includeDelegated = product?.activeProduct === 'Estudio';
  const { data, isLoading } = useQuery({
    queryKey: ['cuit-profiles', 'list', includeDelegated],
    queryFn: () => cuitProfilesApi.listCuitProfiles(0, 200, { includeDelegated }),
  });

  const rawProfiles = data?.items ?? [];
  const activeCommercialProduct = product?.activeCommercialProduct;
  const isOwnCuitProduct = activeCommercialProduct === 'Monotributo'
    || activeCommercialProduct === 'ResponsableInscripto';
  const profiles = useMemo(
    () => filterCuitProfilesForCommercialProduct(rawProfiles, activeCommercialProduct),
    [activeCommercialProduct, rawProfiles],
  );
  const filteredProfiles = useMemo(
    () => profiles.filter((profile) => (
      (environmentFilter === 'All' || profile.environment === environmentFilter)
      && (statusFilter === 'All' || profile.status === statusFilter)
    )),
    [environmentFilter, profiles, statusFilter],
  );

  const environmentCounts = countBy(profiles, (profile) => profile.environment);
  const statusCounts = countBy(profiles, (profile) => profile.status);
  const hasFilters = environmentFilter !== 'All' || statusFilter !== 'All';
  const distinctCuits = new Set(profiles.map((profile) => profile.cuit));
  const canCreateProfile = !isOwnCuitProduct || distinctCuits.size === 0;
  const pageTitle = isOwnCuitProduct ? 'Mi AFIP' : 'Contribuyentes';
  const pageSubtitle = isOwnCuitProduct
    ? 'Gestioná tu CUIT, certificado, puntos de venta y conexión con ARCA desde un solo lugar.'
    : 'Administrá CUITs, puntos de venta y configuración fiscal desde un solo lugar.';

  if (isLoading) return <LoadingScreen />;

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} sx={{ mb: 3, gap: 2 }}>
        <Box>
          <Typography variant="h4">{pageTitle}</Typography>
          <Typography variant="body2" color="text.secondary">
            {pageSubtitle}
          </Typography>
        </Box>
        {profiles.length > 0 && canCreateProfile ? (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate({ to: '/cuit-profiles/new' })}
          >
            {isOwnCuitProduct ? 'Crear mi CUIT' : 'Nuevo CUIT'}
          </Button>
        ) : null}
      </Stack>

      {profiles.length === 0 ? (
        <EmptyState
          icon={<Business sx={{ fontSize: 56 }} />}
          title={isOwnCuitProduct ? 'Todavía no configuraste tu AFIP' : 'Todavía no agregaste contribuyentes'}
          description={isOwnCuitProduct
            ? 'Registrá tu CUIT para configurar ARCA, cargar certificados y empezar a emitir desde Tilde.'
            : 'Registrá tu primer CUIT para configurar ARCA y empezar a emitir comprobantes.'}
          actionLabel={isOwnCuitProduct ? 'Configurar mi CUIT' : 'Crear primer CUIT'}
          onAction={() => navigate({ to: '/cuit-profiles/new' })}
        />
      ) : (
        <Stack spacing={2.25}>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              alignItems: 'center',
            }}
          >
            <FilterPill
              label="Todos los ambientes"
              count={profiles.length}
              selected={environmentFilter === 'All'}
              onClick={() => setEnvironmentFilter('All')}
            />
            {(['Production', 'Homologation'] as const).map((environment) => (
              <FilterPill
                key={environment}
                label={AFIP_ENVIRONMENT_LABEL[environment]}
                count={environmentCounts[environment] ?? 0}
                selected={environmentFilter === environment}
                color={environment === 'Production' ? 'error' : 'info'}
                onClick={() => setEnvironmentFilter(environment)}
              />
            ))}
            <Box sx={{ width: { xs: '100%', md: 12 } }} />
            <FilterPill
              label="Todos los estados"
              count={profiles.length}
              selected={statusFilter === 'All'}
              onClick={() => setStatusFilter('All')}
            />
            {(['Active', 'PendingCertificate', 'Inactive'] as const).map((status) => (
              <FilterPill
                key={status}
                label={CUIT_PROFILE_STATUS_LABEL[status]}
                count={statusCounts[status] ?? 0}
                selected={statusFilter === status}
                color={status === 'Active' ? 'success' : status === 'PendingCertificate' ? 'warning' : 'default'}
                onClick={() => setStatusFilter(status)}
              />
            ))}
          </Box>

          {filteredProfiles.length === 0 ? (
            <EmptyState
              icon={<Business sx={{ fontSize: 48 }} />}
              title={isOwnCuitProduct ? 'No hay perfiles de tu CUIT con esos filtros' : 'No hay contribuyentes con esos filtros'}
              description="Probá cambiar el ambiente o el estado para ver otros registros."
              actionLabel={hasFilters ? 'Limpiar filtros' : undefined}
              onAction={hasFilters ? () => {
                setEnvironmentFilter('All');
                setStatusFilter('All');
              } : undefined}
            />
          ) : (
            <Grid container spacing={2}>
              {filteredProfiles.map((p) => (
                <Grid key={p.id} size={{ xs: 12, sm: 6, md: 4 }}>
                  <CuitProfileCard profile={p} />
                </Grid>
              ))}
            </Grid>
          )}
        </Stack>
      )}
    </Box>
  );
}

function countBy<T extends string>(
  items: CuitProfileResponse[],
  select: (item: CuitProfileResponse) => T,
): Partial<Record<T, number>> {
  return items.reduce<Partial<Record<T, number>>>((acc, item) => {
    const key = select(item);
    acc[key] = (acc[key] ?? 0) + 1;
    return acc;
  }, {});
}

function FilterPill({
  label,
  count,
  selected,
  color = 'primary',
  onClick,
}: {
  label: string;
  count: number;
  selected: boolean;
  color?: 'default' | 'primary' | 'success' | 'warning' | 'error' | 'info';
  onClick: () => void;
}): JSX.Element {
  return (
    <Chip
      clickable
      label={`${label} · ${count}`}
      color={selected ? color : 'default'}
      variant={selected ? 'filled' : 'outlined'}
      onClick={onClick}
      sx={{
        borderRadius: 999,
        fontWeight: selected ? 750 : 600,
        bgcolor: selected && color === 'default' ? 'text.primary' : undefined,
        color: selected && color === 'default' ? 'background.paper' : undefined,
      }}
    />
  );
}
